// GENERAL
import { Fragment, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ColumnInstance } from "react-table";

// HOOKS & UTILS
import useClickOutsideOfElement from "../../../../../../hooks/useClickOutsideOfElement";
import {
	useDataTableContext,
	usePrimaryTableHeaderModel,
} from "../../../hooks";
import { getFilterConfigByType } from "../../../../../../utils";

// COMPONENTS
import BaseHeaderCell from "./BaseHeaderCell";
import HeaderCellInfiniteScroll from "./HeaderCellInfiniteScroll";

// TYPES
import { CustomColumn, PrimaryTableHeaderProps } from "../../../types";

// DUMMY FIELDS
import { FILTERFIELDS } from "../../FIELDS/FILTERFIELDS";

const ItemTableHeader = <T extends object>({
	headerGroups,
	editingHeader,
	searchText,
	setSortColumn,
	setEditingHeader,
	setHeaderValue,
	columnMappings,
	setSearchColumn,
	isNested,
	parentIndex,
	onHeaderSortClick,
	slug,
	searchResultsPillColor,
	isTableSearchAndSortEnabled = true,
}: PrimaryTableHeaderProps<T>): JSX.Element => {
	const {
		searchCriteria,
		setSearchCriteria,
		hasInfiniteScroll,
		leftLockedColumns,
		isNestedChildTable,
		setColumnHeaders,
		displayStickyColumn,
	} = useDataTableContext();
	const {
		mappingFields,
		containerRef,
		magnifyingIconRef,
		inputRef,
		handleSortChange,
	} = usePrimaryTableHeaderModel<T>({
		headerGroups,
		columnMappings,
		onHeaderSortClick,
		editingHeader,
		setEditingHeader,
		slug,
		searchCriteria,
		setSearchCriteria,
	});

	const { search } = useLocation();

	// build the exact list of slugs you see in the URL
	const columnSlugs = useMemo(
		() => columnMappings.map((m) => m.slug),
		[columnMappings],
	);

	const [copyUuid, setCopyUuid] = useState<string | null>(null);

	useClickOutsideOfElement(
		containerRef,
		() => setEditingHeader(null),
		magnifyingIconRef,
	);

	useEffect(() => {
		const stored = localStorage.getItem("parentUuid");
		if (stored) {
			setCopyUuid(stored);
		}
	}, []);
	useEffect(() => {
		const texts = headerGroups.flatMap((group) =>
			group.headers
				.map((col) => (col as CustomColumn<T>).headerText)
				.filter((h): h is string => typeof h === "string"),
		);
		setColumnHeaders(texts);
	}, [headerGroups, setColumnHeaders]);

	const activeCols = useMemo(() => {
		const params = new URLSearchParams(search);
		const fromUrl = columnSlugs
			.map((col, i) => (params.has(`${slug}_${col}`) ? i : -1))
			.filter((i) => i >= 0);

		const fromState = searchCriteria
			.map((crit) => {
				return columnSlugs.indexOf(crit.searchColumn.accessor);
			})
			.filter((i) => i >= 0);

		return Array.from(new Set([...fromUrl, ...fromState]));
	}, [search, searchCriteria, columnSlugs, slug]);

	return (
		<thead className="sticky top-0 bg-supply-blue-50 w-full z-50">
			{headerGroups.map((headerGroup, headerGroupIndex) => (
				<tr
					{...headerGroup.getHeaderGroupProps()}
					key={`header-group-${headerGroupIndex}`}
				>
					{headerGroup.headers.map((column, columnIndex) => {
						const isFirstColumn = columnIndex === 0;
						const isLastColumn =
							columnIndex === headerGroup.headers.length - 1;

						const customColumn = column as ColumnInstance<T> & {
							accessor?: string | ((row: T) => any);
							headerText?: string;
							isCopyable?: boolean;
						};
						const isLeftColumnStickyHeader =
							columnIndex < leftLockedColumns;
						const isLastOfLeftStickyColumns =
							columnIndex === leftLockedColumns - 1;

						const columnIsRightActionBar =
							isLastColumn && displayStickyColumn;

						const filterConfig = getFilterConfigByType(
							"",
							FILTERFIELDS,
						);

						if (customColumn.Header === "Inventory Type") {
							return null;
						}

						const columnId = customColumn.id || String(columnIndex);

						const roundedClasses = isFirstColumn
							? "rounded-tl-lg "
							: isLastColumn
								? "rounded-tr-lg "
								: "";
						const mappingField = mappingFields[columnIndex];

						const { filterIcon, filterWeight } = filterConfig;
						const updatedColumn = {
							...customColumn,
							Header: customColumn.Header,
							accessor:
								mappingField ||
								(customColumn as any).accessor ||
								"",
							id: columnId,
						};

						//the header will behave oddly when the virtualized list for the infinite scroll is present, so we have to have this huge conditional just for the styles
						return hasInfiniteScroll ? (
							<Fragment key={`header-cell-${columnId}`}>
								<HeaderCellInfiniteScroll
									roundedClasses={roundedClasses}
									column={customColumn}
									isLeftColumnStickyHeader={
										isLeftColumnStickyHeader
									}
									editingHeader={editingHeader}
									setEditingHeader={setEditingHeader}
									inputRef={inputRef}
									containerRef={containerRef}
									slug={slug}
									mappingField={mappingField}
									searchResultsPillColor={
										searchResultsPillColor
									}
									filterIcon={filterIcon}
									filterWeight={filterWeight}
									activeCols={activeCols}
									columnIndex={columnIndex}
									setSearchColumn={setSearchColumn}
									searchText={searchText}
									setHeaderValue={setHeaderValue}
									isTableSearchAndSortEnabled={
										isTableSearchAndSortEnabled
									}
									parentIndex={parentIndex}
									isNested={isNested}
									setSortColumn={setSortColumn}
									handleSortChange={handleSortChange}
									magnifyingIconRef={magnifyingIconRef}
									columnIsRightActionBar={
										columnIsRightActionBar
									}
									updatedColumn={updatedColumn}
								/>
							</Fragment>
						) : (
							<Fragment key={`header-cell-${columnId}`}>
								<BaseHeaderCell
									column={customColumn}
									isLeftColumnStickyHeader={
										isLeftColumnStickyHeader
									}
									headerGroupIndex={headerGroupIndex}
									columnIndex={columnIndex}
									editingHeader={editingHeader}
									updatedColumn={updatedColumn}
									containerRef={containerRef}
									isLastOfLeftStickyColumns={
										isLastOfLeftStickyColumns
									}
									setEditingHeader={setEditingHeader}
									searchResultsPillColor={
										searchResultsPillColor
									}
									slug={slug}
									mappingField={mappingField}
									inputRef={inputRef}
									columnIsRightActionBar={
										columnIsRightActionBar
									}
									isNestedChildTable={isNestedChildTable}
									isTableSearchAndSortEnabled={
										isTableSearchAndSortEnabled
									}
									setSortColumn={setSortColumn}
									handleSortChange={handleSortChange}
									parentIndex={parentIndex}
									setHeaderValue={setHeaderValue}
									magnifyingIconRef={magnifyingIconRef}
									searchText={searchText}
									activeCols={activeCols}
									setSearchColumn={setSearchColumn}
									filterIcon={filterIcon}
									filterWeight={filterWeight}
								/>
							</Fragment>
						);
					})}
				</tr>
			))}
		</thead>
	);
};

export default ItemTableHeader;
